import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { maskCep, lookupCep } from "../lib/cep";
import { maskCpfCnpj, validateCpfCnpj } from "../lib/cpf";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { UserCircle, MapPin, Briefcase, CurrencyCircleDollar, Users, ChatCircleDots, Receipt, ShoppingBag, FloppyDisk, ArrowSquareOut, Copy, HandCoins, CheckCircle } from "@phosphor-icons/react";
import BRLInput from "../components/BRLInput";

const brl = (v) => ("R$ " + Number(v || 0).toFixed(2)).replace(".", ",");

function Field({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">{label}</label>
      <input type={type} className="input-underline" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#EBE5DF] bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#5C5A57]"><Icon size={14} weight="duotone" /> {label}</div>
      <div className="font-display text-2xl font-bold mt-1 tabular">{value}</div>
    </div>
  );
}

export default function ResellerProfile() {
  const { user } = useAuth();
  const params = useParams();
  const rid = params.id || user?.id;
  const isAdmin = user?.role === "admin";
  const clientBase = isAdmin ? "/admin/clientes" : "/revenda/clientes";

  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepFound, setCepFound] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/users/resellers/${rid}`); setP({ ...data, address: data.address || {}, financeiro: data.financeiro || {} }); }
      catch (e) { toast.error(e.response?.data?.detail || "Erro ao carregar perfil"); }
    })();
  }, [rid]);

  const setF = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setAddr = (k, v) => setP((x) => ({ ...x, address: { ...x.address, [k]: v } }));
  const setFin = (k, v) => setP((x) => ({ ...x, financeiro: { ...x.financeiro, [k]: v } }));

  const onCep = async (v) => {
    const masked = maskCep(v);
    setAddr("cep", masked);
    setCepFound(false);
    if (masked.replace(/\D/g, "").length === 8) {
      setCepLoading(true);
      try {
        const res = await lookupCep(masked);
        if (res.ok) {
          setP((x) => ({ ...x, address: { ...x.address, rua: res.data.street || x.address.rua, bairro: res.data.district || x.address.bairro, cidade: res.data.city || x.address.cidade, estado: res.data.state || x.address.estado } }));
          setCepFound(true);
        }
      } catch { /* manual */ } finally { setCepLoading(false); }
    }
  };

  const copyPix = async () => {
    const key = (p.financeiro || {}).pix_key;
    if (!key) return toast.error("Nenhuma chave PIX cadastrada");
    const ok = await copyToClipboard(key);
    if (ok) toast.success("Chave PIX copiada"); else toast.error("Nao foi possivel copiar");
  };

  const save = async () => {
    if (p.cpf_cnpj && p.cpf_cnpj.replace(/\D/g, "").length > 0) {
      const { ok, isCnpj } = validateCpfCnpj(p.cpf_cnpj);
      if (!ok) { toast.error(`${isCnpj ? "CNPJ" : "CPF"} inválido. Verifique os números informados.`); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/users/resellers/${rid}`, {
        name: p.name, phone: p.phone, cpf_cnpj: p.cpf_cnpj, email: p.email, birth_date: p.birth_date,
        address: p.address, comissao_padrao: p.comissao_padrao === "" ? null : Number(p.comissao_padrao),
        meta_mensal: p.meta_mensal === "" ? null : Number(p.meta_mensal), regiao: p.regiao, observacoes: p.observacoes,
        financeiro: p.financeiro,
        ...(isAdmin ? { can_edit_sales: !!p.can_edit_sales } : {}),
      });
      setP({ ...data, address: data.address || {}, financeiro: data.financeiro || {} });
      toast.success("Perfil salvo");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  if (!p) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="reseller-profile-loading">Carregando...</div>;
  const s = p.stats || {};
  const fin = p.financeiro || {};

  return (
    <div data-testid="reseller-profile-page" className="max-w-5xl">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">{isAdmin ? "Revendedora" : "Minha conta"}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{p.name}</h1>
          <div className="text-xs text-[#5C5A57] mt-1">{p.email} · cadastrada em {formatDate(p.created_at)} · <span className={`badge ${p.is_active !== false ? "green" : "red"}`}>{p.is_active !== false ? "Ativa" : "Inativa"}</span></div>
        </div>
        <UserCircle size={42} weight="duotone" />
      </div>

      {/* Stats / relacionamentos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="reseller-stats">
        <Stat icon={Users} label="Clientes" value={s.clients ?? 0} />
        <Stat icon={ShoppingBag} label="Vendas" value={`${s.sales_total ?? 0} · ${brl(s.sales_value)}`} />
        <Stat icon={CurrencyCircleDollar} label="Parcelas abertas" value={`${s.open_installments ?? 0} · ${brl(s.open_amount)}`} />
        <Stat icon={Receipt} label="Comprovantes" value={`${s.receipts_pending ?? 0} pend. / ${s.receipts_total ?? 0}`} />
      </div>
      <div className="flex flex-wrap gap-3 mb-8 text-xs">
        <Link to={isAdmin ? "/admin/crm" : "/revenda/crm"} className="inline-flex items-center gap-1 tracking-widest uppercase font-medium hover:underline" data-testid="link-crm"><ChatCircleDots size={14} /> Conversas no CRM</Link>
        <Link to={isAdmin ? "/admin/comprovantes" : "/revenda/comprovantes"} className="inline-flex items-center gap-1 tracking-widest uppercase font-medium hover:underline" data-testid="link-receipts"><Receipt size={14} /> Comprovantes</Link>
      </div>

      {/* PIX direto da revendedora */}
      <section className="border border-[#3F6B45] bg-white p-6 mb-8" data-testid="reseller-pix-direct">
        <h2 className="section-title flex items-center gap-2 mb-4"><HandCoins size={18} weight="duotone" color="#3F6B45" /> PIX direto (rastreabilidade)</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <Stat icon={HandCoins} label="Solicitacoes de PIX direto" value={s.pix_direct_requests ?? 0} />
          <Stat icon={CurrencyCircleDollar} label="Recebido (conferido)" value={brl(s.pix_direct_received)} />
          <Stat icon={Receipt} label="Comprovantes pendentes" value={s.pix_direct_pending ?? 0} />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-2">Clientes que mais solicitam PIX direto</div>
          {(s.pix_direct_top_clients || []).length === 0 ? (
            <div className="text-sm text-[#5C5A57]">Nenhuma solicitacao registrada ainda.</div>
          ) : (
            <ul className="space-y-1" data-testid="pix-direct-top-clients">
              {(s.pix_direct_top_clients || []).map((c, idx) => (
                <li key={idx} className="flex items-center justify-between text-sm border-b border-[#F0EBE5] py-1">
                  <span>{c.name}</span><span className="tabular font-medium">{c.count}x</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pessoais */}
        <section className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title flex items-center gap-2 mb-5"><UserCircle size={18} weight="duotone" /> Dados pessoais</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome completo" value={p.name} onChange={(v) => setF("name", v)} testid="rp-name" />
            <Field label="Telefone" value={p.phone} onChange={(v) => setF("phone", v)} placeholder="(11) 90000-0000" testid="rp-phone" />
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CPF / CNPJ</label>
              <input className="input-underline" value={p.cpf_cnpj || ""} onChange={(e) => setF("cpf_cnpj", maskCpfCnpj(e.target.value))} placeholder="000.000.000-00" inputMode="numeric" maxLength={18} data-testid="rp-cpf" />
              {p.cpf_cnpj && [11, 14].includes(p.cpf_cnpj.replace(/\D/g, "").length) && !validateCpfCnpj(p.cpf_cnpj).ok && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="rp-cpf-error">{p.cpf_cnpj.replace(/\D/g, "").length === 14 ? "CNPJ inválido. Verifique os números informados." : "CPF inválido. Verifique os números informados."}</div>}
            </div>
            <Field label="E-mail" value={p.email} onChange={(v) => setF("email", v)} type="email" testid="rp-email" />
            <Field label="Data de nascimento" value={p.birth_date} onChange={(v) => setF("birth_date", v)} type="date" testid="rp-birth" />
          </div>
        </section>

        {/* Endereco */}
        <section className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title flex items-center gap-2 mb-5"><MapPin size={18} weight="duotone" /> Endereco</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CEP {cepLoading && <span className="text-[#5C5A57]">(Buscando endereço...)</span>}</label>
              <input className="input-underline" value={p.address.cep || ""} onChange={(e) => onCep(e.target.value)} placeholder="00000-000" inputMode="numeric" maxLength={9} data-testid="rp-cep" />
              {cepFound && !cepLoading && <div className="text-[10px] text-[#3F6B45] mt-1 flex items-center gap-1" data-testid="rp-cep-success"><CheckCircle size={12} weight="fill" /> Endereço localizado com sucesso.</div>}
            </div>
            <Field label="Rua" value={p.address.rua} onChange={(v) => setAddr("rua", v)} testid="rp-rua" />
            <Field label="Numero" value={p.address.numero} onChange={(v) => setAddr("numero", v)} testid="rp-numero" />
            <Field label="Complemento" value={p.address.complemento} onChange={(v) => setAddr("complemento", v)} testid="rp-complemento" />
            <Field label="Bairro" value={p.address.bairro} onChange={(v) => setAddr("bairro", v)} testid="rp-bairro" />
            <Field label="Cidade" value={p.address.cidade} onChange={(v) => setAddr("cidade", v)} testid="rp-cidade" />
            <Field label="Estado" value={p.address.estado} onChange={(v) => setAddr("estado", v)} testid="rp-estado" />
          </div>
        </section>

        {/* Comercial */}
        <section className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title flex items-center gap-2 mb-5"><Briefcase size={18} weight="duotone" /> Dados comerciais</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Comissao padrao (%)" value={p.comissao_padrao} onChange={(v) => setF("comissao_padrao", v)} type="number" testid="rp-comissao" />
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Meta mensal (R$)</label>
              <BRLInput value={p.meta_mensal} onChange={(v) => setF("meta_mensal", v)} testid="rp-meta" />
            </div>
            <Field label="Regiao / Praca" value={p.regiao} onChange={(v) => setF("regiao", v)} testid="rp-regiao" />
          </div>
          <div className="mt-4">
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Observacoes</label>
            <textarea rows={2} className="w-full p-3 border border-[#EBE5DF] text-sm" value={p.observacoes || ""} onChange={(e) => setF("observacoes", e.target.value)} data-testid="rp-obs" />
          </div>
          {isAdmin && (
            <div className="mt-5 pt-4 border-t border-[#EBE5DF]" data-testid="rp-permissions">
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-2">Permissoes</div>
              <button type="button" onClick={() => setF("can_edit_sales", !p.can_edit_sales)} className="flex items-center justify-between w-full text-left" data-testid="rp-can-edit-sales-toggle">
                <span className="text-sm">Permitir editar vendas finalizadas</span>
                <span className={`badge ${p.can_edit_sales ? "green" : ""}`}>{p.can_edit_sales ? "Habilitado" : "Desabilitado"}</span>
              </button>
              <div className="text-[11px] text-[#5C5A57] mt-1">Somente o administrador altera. A revendedora so podera editar vendas proprias, com auditoria completa.</div>
            </div>
          )}
        </section>

        {/* Financeiro / PIX */}
        <section className="border border-[#3F6B45] bg-white p-6" data-testid="rp-financeiro">
          <h2 className="section-title flex items-center gap-2 mb-1"><CurrencyCircleDollar size={18} weight="duotone" color="#3F6B45" /> Dados financeiros (PIX)</h2>
          <div className="text-[11px] text-[#5C5A57] mb-4 bg-[#EAF1EA] border border-[#cfe0cf] px-2 py-1.5">Usado apenas quando a cliente solicitar pagamento direto. <strong>Nunca enviado automaticamente.</strong></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Tipo da chave</label>
              <select className="input-underline" value={fin.pix_key_type || ""} onChange={(e) => setFin("pix_key_type", e.target.value)} data-testid="rp-pix-type">
                <option value="">Selecione...</option>
                {["CPF", "CNPJ", "E-mail", "Telefone", "Aleatoria"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Chave PIX</label>
              <div className="flex items-center gap-2">
                <input className="input-underline flex-1" value={fin.pix_key || ""} onChange={(e) => setFin("pix_key", e.target.value)} data-testid="rp-pix-key" />
                <button type="button" onClick={copyPix} title="Copiar chave PIX" className="shrink-0 inline-flex items-center gap-1 border border-[#3F6B45] text-[#3F6B45] px-3 py-2 text-[10px] tracking-widest uppercase font-medium hover:bg-[#EAF1EA]" data-testid="rp-pix-copy-btn"><Copy size={14} /> Copiar</button>
              </div>
            </div>
            <Field label="Titular da chave" value={fin.pix_holder} onChange={(v) => setFin("pix_holder", v)} testid="rp-pix-holder" />
            <Field label="Banco" value={fin.pix_bank} onChange={(v) => setFin("pix_bank", v)} testid="rp-pix-bank" />
          </div>
          <div className="mt-4">
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Observacoes</label>
            <textarea rows={2} className="w-full p-3 border border-[#EBE5DF] text-sm" value={fin.pix_notes || ""} onChange={(e) => setFin("pix_notes", e.target.value)} data-testid="rp-pix-notes" />
          </div>
        </section>
      </div>

      <button onClick={save} disabled={saving} className="brand mt-6 inline-flex items-center gap-2" data-testid="rp-save-btn"><FloppyDisk size={16} /> {saving ? "Salvando..." : "Salvar perfil"}</button>

      {/* Clientes vinculados */}
      <section className="mt-8" data-testid="rp-clients">
        <h2 className="section-title flex items-center gap-2 mb-3"><Users size={18} weight="duotone" /> Clientes vinculados ({(p.clients || []).length})</h2>
        <div className="table-wrap">
          <table className="crl">
            <thead><tr><th>Nome</th><th>Telefone</th><th>Status</th><th>Score</th><th>Saldo aberto</th></tr></thead>
            <tbody>
              {(p.clients || []).map((c) => (
                <tr key={c.id} data-testid={`rp-client-${c.id}`}>
                  <td><Link to={`${clientBase}/${c.id}`} className="font-medium hover:underline inline-flex items-center gap-1">{c.name} <ArrowSquareOut size={12} /></Link></td>
                  <td className="text-[#5C5A57]">{c.phone}</td>
                  <td><span className={`badge ${c.status === "blocked" ? "red" : c.status === "defaulter" ? "medio" : "green"}`}>{c.status || "ativo"}</span></td>
                  <td className="tabular">{c.score ?? "-"}</td>
                  <td className="tabular">{brl(c.open_balance)}</td>
                </tr>
              ))}
              {(p.clients || []).length === 0 && <tr><td colSpan="5" className="text-center text-sm text-[#5C5A57] py-8">Nenhum cliente vinculado.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
